import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { GoogleMap, Marker, Polyline } from '@react-google-maps/api';
import { nanoid } from 'nanoid';
import { THEME } from '../../common/constants';
import { ICONS_LABELS } from '../Segments/SegmentPhotos/constants';

const containerStyle = {
    width: '100%',
    height: '50vh',
};

/**
 * AnalyzeMap Component
 * @param {number} zoomLevel
 * @param {Array} dataPoints
 */
function AnalyzeMap({ zoomLevel, dataPoints }) {
    const [map, setMap] = useState(null);
    const onLoad = useCallback((map) => setMap(map), []);

    useEffect(() => {
        if (map) {
            const bounds = new window.google.maps.LatLngBounds();
            dataPoints.map((marker) => {
                if (marker.position) {
                    // if data points are locations
                    bounds.extend(marker.position);
                } else if (marker.markerDataPoints) {
                    // if data points are segments
                    marker.markerDataPoints.map((segment) => {
                        bounds.extend(segment.position);
                    });
                }
            });
            map.fitBounds(bounds);
        }
    }, [map, dataPoints]);

    const renderMarkers = () => {
        if (dataPoints && dataPoints.length > 0) {
            return dataPoints.map((marker) => {
                // if data contains litter data for each segment
                if (marker && marker.markerDataPoints) {
                    return marker.markerDataPoints.map((litter) => {
                        return (
                            <Marker
                                key={nanoid()}
                                position={litter.position}
                                className="mapMarker"
                                icon={
                                    ICONS_LABELS[litter.classification] ||
                                    ICONS_LABELS['LITTER']
                                }
                            />
                        );
                    });
                } else {
                    // if data is only a list of locations
                    return (
                        <Marker
                            key={nanoid()}
                            position={marker.position}
                            className="mapMarker"
                        />
                    );
                }
            });
        }
    };

    const renderPolyline = () => {
        if (dataPoints && dataPoints.length > 0) {
            return dataPoints.map((segment) => {
                return (
                    <Polyline
                        key={nanoid()}
                        path={segment.location}
                        options={{
                            strokeColor: THEME.PRIMARY_BLUE,
                        }}
                    />
                );
            });
        }
    };

    const mapStyles = [
        { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
        { featureType: 'poi.attraction', stylers: [{ visibility: 'off' }] },
    ];

    return (
        <div className="col-12">
            <GoogleMap
                mapContainerStyle={containerStyle}
                zoom={zoomLevel}
                options={{
                    streetViewControl: false,
                    mapTypeControl: false,
                    styles: mapStyles,
                }}
                onLoad={onLoad}
            >
                {/* Child components, such as markers, info windows, etc. */}
                {renderMarkers()}
                {renderPolyline()}
            </GoogleMap>
        </div>
    );
}

AnalyzeMap.propTypes = {
    zoomLevel: PropTypes.number,
    dataPoints: PropTypes.array,
};

AnalyzeMap.defaultProps = {
    zoomLevel: 18,
};

export default React.memo(AnalyzeMap);
